import { useMemo } from 'react';
import { Iterable } from 'immutable';

import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectChatSessionAdmins,
    selectChatSessionMembers,
    selectChatSessionRoomType,
} from 'flow/selectors/chat-selectors';
import { selectOwnChatUserKey } from 'flow/selectors/own-extension-selectors';
import { ChatMember } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { sortByName } from 'flow/utils';
import { roomTypes } from 'flow/utils/message-utils-TW';

interface ChatMembersHook {
    /* Iterable, not List: filter and sort hand back an Iterable in
     * Immutable 3, and the caller only maps over it. */
    chatMembers: Iterable<number, TypedMap<ChatMember>>;
    canAddMember: boolean;
}

/**
 * The members of a chat, ordered for display: you first, then admins, the rest
 * by name, and anyone removed left out.
 *
 * Returns only what it derives. The session itself is deliberately not handed
 * back: returning it made every caller subscribe to an object rewritten on
 * every message and every read receipt, and re-render for changes it never
 * read. Anything a caller wants unchanged - the admins, whether the session
 * has loaded - it selects for itself, so nothing here is a pass-through.
 */
const useChatMembers = (chatSessionKey: ChatSessionKey): ChatMembersHook => {
    const ownChatUserKey = useAppSelector(selectOwnChatUserKey);
    const members = useAppSelector((state: State) =>
        selectChatSessionMembers(state, chatSessionKey),
    );
    const admins = useAppSelector((state: State) =>
        selectChatSessionAdmins(state, chatSessionKey),
    );
    const roomType = useAppSelector((state: State) =>
        selectChatSessionRoomType(state, chatSessionKey),
    );
    /* Two filters and two sorts, so it is worth not repeating them for a
     * membership that did not change. Structural sharing keeps members and
     * admins the same objects while the session around them is rewritten, so
     * keying on them recomputes only when the membership really changed. A
     * sessions refetch does replace them - Immutable 3 merges without
     * comparing - so it recomputes there, which is rare and right. */
    const chatMembers = useMemo(() => {
        const isOwn = (member: TypedMap<ChatMember>) =>
            member.get('key') === ownChatUserKey;
        const isAdmin = (member: TypedMap<ChatMember>) =>
            admins.includes(member.get('key'));

        return members
            .filter((i) => !!i)
            .filter((member) => member.get('removed') !== true)
            .sort(sortByName)
            .sort((a, b) => {
                if (isOwn(a)) {
                    return -1;
                }

                if (isOwn(b)) {
                    return 1;
                }

                if (isAdmin(a)) {
                    return isAdmin(b) ? 0 : -1;
                }

                if (isAdmin(b)) {
                    return 1;
                }

                return 0;
            });
    }, [members, admins, ownChatUserKey]);

    const canAddMember =
        roomType === roomTypes.session || admins.includes(ownChatUserKey);

    return { chatMembers, canAddMember };
};

export default useChatMembers;
